import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import kebabCase from 'lodash/kebabCase';
import omit from 'lodash/omit';
import Transition from 'react-transition-group/Transition';

/**
 * AnimateHeight - A component to animate the height gracefully in a declarative way.
 * Please see the props docs to understand how to use this component.
 */
export default class AnimateHeight extends PureComponent {
  constructor(props) {
    super(props);
    this._updateScrollHeightIfNeeded = this._updateScrollHeightIfNeeded.bind(this);
    this._handleAddEndListener = this._handleAddEndListener.bind(this);
    this._setContainerRef = this._setContainerRef.bind(this);

    this.state = {
      containerStyle: this.getContainerStyle(props),
      scrollHeight: null
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // If we're about to open, save scrollHeight before this render cycle so we can compare it
    // with the scrollHeight after this render cycle.
    this._updateScrollHeightIfNeeded(this.props, nextProps);

    if (
      nextProps.style !== this.props.style ||
      nextProps.height !== this.props.height ||
      nextProps.timeout !== this.props.timeout ||
      nextProps.heightPropertyName !== this.props.heightPropertyName ||
      nextProps.isOpen !== this.props.isOpen
    ) {
      this.setState({ containerStyle: this.getContainerStyle(nextProps) });
    }
  }

  componentDidUpdate(prevProps) {
    // In case the content height changed at the same render cycle we got `isOpen: true`, we should
    // force update so we'll get the correct height in `render`.
    this._updateScrollHeightIfNeeded(prevProps, this.props);
  }

  _updateScrollHeightIfNeeded(prevProps, nextProps) {
    // Only once the latest props have changed to open the animate height we want to just think about re-rendering.
    if (nextProps.isOpen && !prevProps.isOpen) {
      const scrollHeight = this.animationContainerRef
        ? this.animationContainerRef.scrollHeight
        : null;
      if (scrollHeight !== this.state.scrollHeight) {
        this.setState({ scrollHeight, containerStyle: this.getContainerStyle(nextProps) });
      }
    } else if (!nextProps.isOpen && prevProps.isOpen) {
      this.setState({ scrollHeight: 0, containerStyle: this.getContainerStyle(nextProps) });
    }
  }

  getContainerStyle(props) {
    const { timeout, height, heightPropertyName, style, isOpen } = props;
    const kebabHeightPropertyName = kebabCase(heightPropertyName);
    const mandatoryStyles = {
      overflowY: 'hidden',
      transitionProperty: kebabHeightPropertyName,
      willChange: kebabHeightPropertyName
    };
    const propsStyle = {};

    if (timeout) {
      propsStyle.transitionDuration = `${timeout}ms`;
    }

    if (height) {
      propsStyle[heightPropertyName] = height;
    }

    const defaultStyle = {
      ...propsStyle,
      ...style,
      ...mandatoryStyles
    };

    const enterStyle = this.animationContainerRef
      ? { ...defaultStyle, [heightPropertyName]: `${this.animationContainerRef.scrollHeight}px` }
      : defaultStyle;

    const enteredStyle = omit(enterStyle, 'overflowY');
    // Dynamically adjust the height in case the content of the container changes
    // we do this only when isOpen is true since we don`t want to interrupt the closing transition
    if (isOpen) {
      enteredStyle[heightPropertyName] = 'auto';
    }

    // The reason we're rebinding this function here is because that function depends on this.state.containerStyle.
    // And this.state.containerStyle is the return value of the current function (getContainerStyle).
    // So this causes the component that uses this function to re-render when needed.
    this.renderChildren = this.renderChildren.bind(this);

    return {
      entered: enteredStyle,
      entering: enterStyle,
      exiting: defaultStyle,
      exited: defaultStyle
    };
  }

  _handleAddEndListener(node, done) {
    node.addEventListener('transitionend', (...args) => {
      this.props.onTransitionEnd(...args);
      return done(...args);
    });
  }

  _setContainerRef(ref) {
    this.animationContainerRef = ref;
  }

  renderChildren(state) {
    // The reason we're destructuring all of these props even though we're not using them because we don't want to pass them along to the internal container.
    // That way we're only left with the properties that might be relevant to the internal container.
    /* eslint-disable no-unused-vars */
    const {
      children,
      timeout,
      isOpen,
      heightPropertyName,
      onTransitionEnd,
      height,
      style,
      onExited,
      onEntered,
      ...rest
    } = this.props;
    /* eslint-enable no-unused-vars */
    const childrenToRender = typeof children === 'function' ? children(state) : children;

    return (
      <div ref={this._setContainerRef} style={this.state.containerStyle[state]} {...rest}>
        {childrenToRender}
      </div>
    );
  }

  render() {
    const { timeout, isOpen, onExited, onEntered } = this.props;

    return (
      <Transition
        in={isOpen}
        timeout={timeout}
        addEndListener={this._handleAddEndListener}
        onEntered={onEntered}
        onExited={onExited}
      >
        {this.renderChildren}
      </Transition>
    );
  }
}

// Where is MIN_HEIGHT?
// If you think about it, when you animate min-height it needs to shrink not grow.
// And this component animates from a small height value to "auto" which is in most cases bigger than the given height.
// It's exactly the reverse behavior of max-height.
// So you can use max-height and reverse the isOpen flag and that should give you the reverse behavior.
AnimateHeight.HeightPropertyNames = {
  HEIGHT: 'height',
  MAX_HEIGHT: 'maxHeight'
};

AnimateHeight.defaultProps = {
  heightPropertyName: 'height',
  onTransitionEnd: noop
};

AnimateHeight.propTypes = {
  /**
   * Child to render that his height would be animated.
   */
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  /**
   * When to animate the component open
   */
  isOpen: PropTypes.bool.isRequired,
  /**
   * Optional timeout value to know when the animation ends (instead of using `transition-duration: <number>`).
   */
  timeout: PropTypes.number,
  /**
   * Optional height to animate to when collapsed. (instead of using `max-height: <height>`).
   * This is a string value so you can pass in any size unit you want.
   */
  height: PropTypes.string,
  /**
   * Class to be applied to the animation container.
   * If you're not using `timeout` or `style` this class should apply `transition-duration: <number>`.
   * If you're not using `height` or `style` this class should apply `max-height: <height>`.
   */
  className: PropTypes.string,
  /**
   * Style to be applied to the animation container.
   * If you're not using `timeout` or `className` this style should apply `transition-duration: <number>`.
   * If you're not using `height` or `className` this style should apply `max-height: <height>`.
   */
  style: PropTypes.object,
  /**
   * Callback to run when the transition ends.
   */
  onTransitionEnd: PropTypes.func,
  /**
   * The name of the height property to animate (defaults to 'height')
   * Allowed options are 'height' or 'maxHeight'.
   */
  heightPropertyName: PropTypes.oneOf(
    Object.keys(AnimateHeight.HeightPropertyNames).map(k => AnimateHeight.HeightPropertyNames[k])
  ),

  /**
   * Callback function to be called when the "in" transition is complete
   */
  onEntered: PropTypes.func,

  /**
   * Callback function to be called when the "exit" transition is complete
   */
  onExited: PropTypes.func
};
